import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, Workflow, Users, X, Shuffle, List, Network, Info, AlertTriangle, Clock, UserPlus } from "lucide-react";
import { useConfirm, usePromptDialog } from "@/components/ConfirmProvider";

const PALETTE = ["#6366f1","#10b981","#f59e0b","#06b6d4","#ec4899","#8b5cf6","#ef4444","#22c55e"];

export default function WorkflowBuilder({ mandanteId, mandanteName, contratoName = "", canEdit, canAssign }) {
  const effCanAssign = canAssign === undefined ? canEdit : canAssign;
  const confirmAction = useConfirm();
  const promptAction = usePromptDialog();
  const [stages, setStages] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [funnel, setFunnel] = useState({ stages: [], initial_count: 0, total_workers: 0 });
  const [editing, setEditing] = useState(null); // stage object or {new:true}
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("flow"); // 'flow' | 'list'
  const [assignStage, setAssignStage] = useState(null); // {id, name} or {id:null,name:'Estado inicial'}

  const reload = async () => {
    setLoading(true);
    try {
      const [s, w, f] = await Promise.all([
        api.get(`/mandantes/${mandanteId}/workflow`).then((r) => r.data),
        api.get("/workers").then((r) => r.data),
        api.get(`/mandantes/${mandanteId}/workflow/funnel`).then((r) => r.data).catch(() => ({ stages: [], initial_count: 0, total_workers: 0 })),
      ]);
      setStages(s);
      const filtered = w.filter((x) => (x.mandante || x.company) === mandanteName && (!contratoName || x.contrato === contratoName));
      setWorkers(filtered);
      // Si estamos restringidos a un contrato, recomputamos el funnel localmente
      if (contratoName) {
        const now = new Date();
        const recomp = (f.stages || []).map((st) => {
          const inStage = filtered.filter((x) => x.workflow_stage_id === st.id);
          const hrs = inStage.map((x) => x.workflow_stage_entered_at ? Math.max(0, (now - new Date(x.workflow_stage_entered_at)) / 36e5) : 0);
          const avg = hrs.length ? hrs.reduce((a, b) => a + b, 0) / hrs.length : 0;
          return { ...st, count: inStage.length, avg_hours_current: Math.round(avg * 10) / 10, bottleneck: avg >= 48 && hrs.some((h) => h >= 48), waiting_long_count: hrs.filter((h) => h >= 48).length };
        });
        setFunnel({ ...f, stages: recomp, initial_count: filtered.filter((x) => !x.workflow_stage_id).length, total_workers: filtered.length });
      } else {
        setFunnel(f);
      }
    } catch (e) { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [mandanteId, contratoName]);

  const columns = useMemo(() => {
    const groups = [];
    const seen = new Set();
    stages.forEach((s) => {
      const key = s.parallel_group ? `g:${s.parallel_group}` : `s:${s.id}`;
      if (!seen.has(key)) {
        seen.add(key);
        groups.push({ key, stages: stages.filter((x) => (x.parallel_group ? `g:${x.parallel_group}` : `s:${x.id}`) === key) });
      }
    });
    return groups;
  }, [stages]);

  const countAt = (sid) => workers.filter((w) => w.workflow_stage_id === sid).length;
  const countInitial = workers.filter((w) => !w.workflow_stage_id).length;
  const total = workers.length;
  const funnelById = useMemo(() => Object.fromEntries((funnel.stages || []).map((s) => [s.id, s])), [funnel]);
  const formatHours = (h) => {
    if (h == null) return null;
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  const existingGroups = useMemo(() => {
    const set = new Set();
    stages.forEach((s) => { if (s.parallel_group) set.add(s.parallel_group); });
    return Array.from(set);
  }, [stages]);

  const renameGroup = async (oldName) => {
    const next = await promptAction({ title: `Renombrar grupo "${oldName}"`,
      description: "Las etapas de este grupo quedarán agrupadas con el nuevo nombre.",
      defaultValue: oldName, placeholder: "Nuevo nombre del grupo", confirmText: "Renombrar" });
    if (next === null) return;
    const name = next.trim().toUpperCase();
    if (!name || name === oldName) return;
    try {
      const affected = stages.filter((s) => s.parallel_group === oldName);
      await Promise.all(affected.map((s) => api.put(`/mandantes/${mandanteId}/workflow/${s.id}`, { parallel_group: name })));
      toast.success(`Grupo renombrado (${affected.length} etapa${affected.length === 1 ? "" : "s"})`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo renombrar"); }
  };

  const removeGroup = async (name) => {
    const affected = stages.filter((s) => s.parallel_group === name);
    const ok = await confirmAction({ title: `Quitar grupo "${name}"`,
      description: `Las ${affected.length} etapa${affected.length === 1 ? "" : "s"} del grupo quedarán secuenciales.`,
      confirmText: "Quitar grupo", variant: "warning" });
    if (!ok) return;
    try {
      await Promise.all(affected.map((s) => api.put(`/mandantes/${mandanteId}/workflow/${s.id}`, { parallel_group: "" })));
      toast.success("Grupo eliminado");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar el grupo"); }
  };

  const remove = async (s) => {
    const ok = await confirmAction({ title: `Eliminar etapa "${s.name}"`,
      description: "Los trabajadores en esta etapa quedarán en estado inicial.",
      confirmText: "Eliminar etapa", variant: "danger" });
    if (!ok) return;
    try { await api.delete(`/mandantes/${mandanteId}/workflow/${s.id}`); toast.success("Etapa eliminada"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };
  const move = async (idx, dir) => {
    const list = [...stages];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setStages(list);
    try { await api.post(`/mandantes/${mandanteId}/workflow/reorder`, { ids: list.map((x) => x.id) }); }
    catch (e) { toast.error("No se pudo reordenar"); reload(); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2">
            <Workflow size={18} className="text-indigo-600" /> Timeline del proceso de acreditación
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Define las etapas que recorren los trabajadores. Agrupa en paralelo poniendo el mismo "Grupo paralelo" en varias etapas.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <div data-testid="workflow-view-toggle" className="inline-flex rounded-md border border-slate-200 bg-slate-50 overflow-hidden text-xs font-semibold">
              <button data-testid="workflow-view-flow" onClick={() => setView("flow")}
                className={`inline-flex items-center gap-1 px-3 h-7 ${view === "flow" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-white"}`}>
                <Network size={12} /> Flujo
              </button>
              <button data-testid="workflow-view-list" onClick={() => setView("list")}
                className={`inline-flex items-center gap-1 px-3 h-7 ${view === "list" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-white"}`}>
                <List size={12} /> Lista
              </button>
            </div>
            <button data-testid="add-stage-btn" onClick={() => setEditing({ new: true, name: "", description: "", parallel_group: "", color: PALETTE[stages.length % PALETTE.length] })}
              className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
              <Plus size={13} /> Nueva etapa
            </button>
          </div>
        )}
      </div>

      {/* GROUP MANAGER */}
      {existingGroups.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-100 bg-amber-50/40 flex items-center gap-2 flex-wrap" data-testid="group-manager">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900">
            <Shuffle size={12} /> Grupos paralelos:
          </span>
          {existingGroups.map((g) => {
            const count = stages.filter((s) => s.parallel_group === g).length;
            return (
              <span key={g} data-testid={`group-chip-${g}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white text-amber-800 border border-amber-200 pl-2 pr-1 py-0.5 rounded-full">
                {g} <span className="text-slate-400 font-normal">({count})</span>
                {canEdit && (
                  <>
                    <button data-testid={`group-edit-${g}`} onClick={() => renameGroup(g)}
                      className="h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Renombrar grupo">
                      <Pencil size={10} />
                    </button>
                    <button data-testid={`group-delete-${g}`} onClick={() => removeGroup(g)}
                      className="h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50" title="Quitar grupo (deja etapas secuenciales)">
                      <X size={10} />
                    </button>
                  </>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* VISUAL FLOW (sólo en vista Flujo) */}
      {view === "flow" && (
      <div className="p-5 bg-gradient-to-r from-slate-50 to-indigo-50/30 border-b border-slate-100 overflow-x-auto">
        {stages.length === 0 ? (
          <p className="text-sm text-slate-400 italic text-center py-4">Agrega la primera etapa para construir el timeline.</p>
        ) : (
          <div className="flex items-center gap-2 min-w-max" data-testid="workflow-flow">
            {/* Estado inicial */}
            <StepCard label="Estado inicial" count={countInitial} total={total} color="#94a3b8" subtitle="Sin asignar aún"
              onAssign={effCanAssign ? () => setAssignStage({ id: null, name: "Estado inicial" }) : null} />
            <Arrow />
            {columns.map((col, i) => (
              <React.Fragment key={col.key}>
                <div className="flex flex-col gap-2">
                  {col.stages.map((s) => {
                    const stat = funnelById[s.id] || {};
                    return (
                      <StepCard key={s.id}
                        label={s.name}
                        description={s.description}
                        count={countAt(s.id)}
                        total={total}
                        color={s.color}
                        parallelTag={col.stages.length > 1 ? s.parallel_group : null}
                        avgHours={formatHours(stat.avg_hours_current)}
                        avgCompleted={formatHours(stat.avg_hours_completed)}
                        bottleneck={stat.bottleneck}
                        waitingLong={stat.waiting_long_count}
                        onAssign={effCanAssign ? () => setAssignStage({ id: s.id, name: s.name }) : null}
                      />
                    );
                  })}
                </div>
                {i < columns.length - 1 && <Arrow />}
              </React.Fragment>
            ))}
            {/* Completado */}
            <Arrow />
            <StepCard label="Acreditado" count={0} total={total} color="#059669" subtitle="Al finalizar la última etapa" />
          </div>
        )}
      </div>
      )}

      {/* STAGE LIST */}
      {loading ? (
        <div className="p-6 text-sm text-slate-400">Cargando…</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-5 py-2 w-12">#</th>
              <th className="px-5 py-2">Etapa</th>
              <th className="px-5 py-2 w-32">Grupo paralelo</th>
              <th className="px-5 py-2 w-24 text-center">Color</th>
              <th className="px-5 py-2 w-32 text-right">Trabajadores</th>
                {canEdit && <th className="px-5 py-2 w-36 text-right"></th>}
                {!canEdit && effCanAssign && <th className="px-5 py-2 w-28 text-right"></th>}
            </tr>
          </thead>
          <tbody>
            {stages.map((s, i) => (
              <tr key={s.id} data-testid={`stage-row-${s.id}`} className="border-t border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-2.5 font-mono text-[11px] text-slate-400">{i + 1}</td>
                <td className="px-5 py-2.5">
                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                  {s.description && <p className="text-xs text-slate-500 line-clamp-1">{s.description}</p>}
                </td>
                <td className="px-5 py-2.5 text-xs text-slate-600">
                  {s.parallel_group
                    ? <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded"><Shuffle size={10} /> {s.parallel_group}</span>
                    : <span className="text-slate-300 italic">Secuencial</span>}
                </td>
                <td className="px-5 py-2.5 text-center">
                  <span className="inline-block h-5 w-5 rounded-full border border-white shadow" style={{ background: s.color }} />
                </td>
                <td className="px-5 py-2.5 text-right">
                  <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">{countAt(s.id)}</span>
                  {(funnelById[s.id]?.avg_hours_current ?? 0) > 0 && (
                    <span className="ml-1 text-[10px] text-slate-500 inline-flex items-center gap-0.5" title="Prom. tiempo en etapa">
                      <Clock size={10} /> {formatHours(funnelById[s.id].avg_hours_current)}
                    </span>
                  )}
                  {funnelById[s.id]?.bottleneck && (
                    <span className="ml-1 text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded" title="Cuello de botella">cuello</span>
                  )}
                </td>
                {canEdit && (
                  <td className="px-5 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`list-assign-${s.id}`} onClick={() => setAssignStage({ id: s.id, name: s.name })}
                      className="h-7 inline-flex items-center justify-center gap-1 px-2 rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-[11px] font-semibold" title="Asignar trabajadores">
                      <UserPlus size={12} /> Asignar
                    </button>
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === stages.length - 1} className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 disabled:opacity-30"><ChevronDown size={14} /></button>
                    <button data-testid={`edit-stage-${s.id}`} onClick={() => setEditing(s)} className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-indigo-600"><Pencil size={13} /></button>
                    <button data-testid={`del-stage-${s.id}`} onClick={() => remove(s)} className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                  </td>
                )}
                {!canEdit && effCanAssign && (
                  <td className="px-5 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`list-assign-${s.id}`} onClick={() => setAssignStage({ id: s.id, name: s.name })}
                      className="h-7 inline-flex items-center justify-center gap-1 px-2 rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-[11px] font-semibold">
                      <UserPlus size={12} /> Asignar
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {stages.length === 0 && <tr><td colSpan={6} className="px-5 py-4 text-sm text-slate-400 italic">Sin etapas definidas.</td></tr>}
          </tbody>
        </table>
      )}

      {editing && (
        <StageDialog mandanteId={mandanteId} initial={editing} existingGroups={existingGroups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
      {assignStage && (
        <AssignWorkersModal mandanteId={mandanteId} mandanteName={mandanteName} stage={assignStage} workers={workers}
          onClose={() => setAssignStage(null)}
          onDone={() => { setAssignStage(null); reload(); }} />
      )}
    </section>
  );
}

function Arrow() {
  return <div className="h-px w-6 bg-slate-300 relative"><span className="absolute -right-1 top-1/2 -translate-y-1/2 border-y-4 border-y-transparent border-l-4 border-l-slate-300" /></div>;
}

function StepCard({ label, description, count = 0, total = 0, color = "#6366f1", subtitle, parallelTag, avgHours, avgCompleted, bottleneck, waitingLong = 0, onAssign }) {
  const pct = total ? Math.round(count / total * 100) : 0;
  return (
    <div className={`relative bg-white rounded-xl border shadow-sm w-56 overflow-hidden ${bottleneck ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}>
      <div className="h-1" style={{ background: color }} />
      <div className="p-3 space-y-1.5">
        <div className="flex items-start justify-between gap-1">
          {parallelTag && (
            <span className="inline-block text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">
              {parallelTag}
            </span>
          )}
          {bottleneck && (
            <span className="inline-flex items-center gap-0.5 text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold uppercase" title={`${waitingLong} trabajador(es) +48h`}>
              <AlertTriangle size={9} /> cuello
            </span>
          )}
        </div>
        <p className="text-xs font-semibold text-slate-800 leading-snug break-words" title={label}>{label}</p>
        {description && <p className="text-[11px] text-slate-400 leading-snug break-words">{description}</p>}
        {subtitle && !description && <p className="text-[11px] text-slate-400 italic">{subtitle}</p>}
        <div className="flex items-center gap-2 pt-1">
          <Users size={12} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-700">{count}</span>
          <span className="text-[10px] text-slate-400">({pct}%)</span>
          {avgHours && (
            <span className="ml-auto text-[10px] text-slate-500 inline-flex items-center gap-0.5" title="Tiempo promedio en etapa (actual)">
              <Clock size={10} /> {avgHours}
            </span>
          )}
        </div>
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </div>
        {avgCompleted && (
          <p className="text-[9px] text-slate-400 italic">Prom. histórico: {avgCompleted}</p>
        )}
        {onAssign && (
          <button data-testid={`assign-stage-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={onAssign}
            className="mt-1 w-full inline-flex items-center justify-center gap-1 h-7 rounded-md border border-dashed border-indigo-200 text-[11px] font-semibold text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50">
            <UserPlus size={11} /> Asignar
          </button>
        )}
      </div>
    </div>
  );
}

function AssignWorkersModal({ mandanteId, mandanteName, stage, workers, onClose, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const filtered = workers.filter((w) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return `${w.first_name || ""} ${w.last_name || ""} ${w.rut || ""} ${w.position || ""}`.toLowerCase().includes(q);
  });
  const allSelected = filtered.length > 0 && filtered.every((w) => selected.has(w.id));
  const toggleAll = () => {
    if (allSelected) { setSelected(new Set()); return; }
    setSelected(new Set(filtered.map((w) => w.id)));
  };
  const submit = async () => {
    if (selected.size === 0) { toast.error("Selecciona al menos un trabajador"); return; }
    setSaving(true);
    try {
      await api.post(`/mandantes/${mandanteId}/workflow/assign`, { stage_id: stage.id, worker_ids: Array.from(selected) });
      toast.success(`${selected.size} trabajador(es) movidos a "${stage.name}"`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo asignar"); }
    finally { setSaving(false); }
  };

  const stageOf = (w) => {
    if (!w.workflow_stage_id) return "Estado inicial";
    return w.workflow_stage_name || "—";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-800">Asignar trabajadores</h3>
            <p className="text-xs text-slate-500 mt-0.5">Mover a <b className="text-slate-800">{stage.name}</b> en {mandanteName}</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <input data-testid="assign-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, RUT o cargo…"
            className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          <button onClick={toggleAll} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 whitespace-nowrap">
            {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400 italic">Sin trabajadores que coincidan.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((w) => (
                <li key={w.id} data-testid={`assign-row-${w.id}`}
                  className={`flex items-center gap-3 px-5 py-2.5 cursor-pointer hover:bg-slate-50 ${selected.has(w.id) ? "bg-indigo-50/60" : ""}`}
                  onClick={() => toggle(w.id)}>
                  <input type="checkbox" checked={selected.has(w.id)} onChange={() => toggle(w.id)} className="h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{w.first_name} {w.last_name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{w.rut} · {w.position || "—"}</p>
                  </div>
                  <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                    {stageOf(w)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50/60">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
          <button data-testid="assign-confirm" onClick={submit} disabled={saving || selected.size === 0}
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Moviendo…" : `Mover ${selected.size} trabajador(es)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StageDialog({ mandanteId, initial, onClose, onSaved, existingGroups = [] }) {
  const isNew = initial.new;
  const [form, setForm] = useState({
    name: initial.name || "",
    description: initial.description || "",
    parallel_group: initial.parallel_group || "",
    color: initial.color || PALETTE[0],
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post(`/mandantes/${mandanteId}/workflow`, form);
      else await api.put(`/mandantes/${mandanteId}/workflow/${initial.id}`, form);
      toast.success(isNew ? "Etapa creada" : "Etapa actualizada");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="stage-dialog">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2">
            <Workflow size={18} className="text-indigo-600" />
            {isNew ? "Nueva etapa" : "Editar etapa"}
          </h3>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Nombre</label>
            <input data-testid="stage-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: En Curso LMS"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Descripción</label>
            <textarea data-testid="stage-description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="¿Qué debe suceder en esta etapa?"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Grupo paralelo (opcional)</label>
            <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 p-2.5 mb-2 text-[11px] text-indigo-800 flex gap-1.5">
              <Info size={13} className="shrink-0 mt-0.5" />
              <p>Las etapas con el <b>mismo nombre de grupo</b> corren en <b>paralelo</b>. Déjalo vacío para que la etapa sea secuencial.</p>
            </div>
            <input data-testid="stage-parallel" value={form.parallel_group}
              list="existing-groups-list"
              onChange={(e) => setForm({ ...form, parallel_group: e.target.value.toUpperCase() })}
              placeholder="Vacío = secuencial · Ej: POST-ACREDITACIÓN"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <datalist id="existing-groups-list">
              {existingGroups.map((g) => <option key={g} value={g} />)}
            </datalist>
            {existingGroups.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="existing-groups">
                <span className="text-[11px] text-slate-400 mr-1">Grupos existentes:</span>
                {existingGroups.map((g) => (
                  <button key={g} type="button" onClick={() => setForm({ ...form, parallel_group: g })}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition ${
                      form.parallel_group === g ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:bg-amber-50"
                    }`}>
                    <Shuffle size={10} /> {g}
                  </button>
                ))}
                {form.parallel_group && (
                  <button type="button" onClick={() => setForm({ ...form, parallel_group: "" })}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-600 px-1.5">
                    <X size={11} /> limpiar
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-2">Color</label>
            <div className="flex gap-2 flex-wrap">
              {PALETTE.map((c) => (
                <button key={c} type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${form.color === c ? "border-slate-900 scale-110" : "border-white"}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
          <button data-testid="save-stage" onClick={save} disabled={saving}
            className="h-10 px-4 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Guardando…" : (isNew ? "Crear etapa" : "Guardar")}
          </button>
        </div>
      </div>
    </div>
  );
}
